import React, {
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import {
  DropDown,
  ErrorMessage,
  Textarea,
} from 'anf-core-react';

import { useOutletContext } from 'react-router';
import TmntContext from '../../context/tmnt';
import ExchangeButton from '../exchange-button/exchange-button';
import TmntText from '../tmnt-text/tmnt-text';
import returnReasonsShape from '../return-reasons-shape';
import { getProductReturnReasonOptions, isSelectionValid } from '../../utils/return-reasons';
import { useGetDigitalDataFlagValue } from '../../utils/digital-data-flag';

export function useReturnReasonLabel() {
  return TmntText({ k: 'DRE_WHY_ARE_YOU_RETURNING', textOnly: true });
}

export function useSelectDetailedReasonLabel() {
  return TmntText({ k: 'DRE_WHAT_IS_PRIMARY_REASON', textOnly: true });
}

export function useTellUsMoreLabel() {
  return TmntText({ k: 'DRE_SELECT_SPECIFIC_DETAIL', textOnly: true });
}

export function useExchangeCommentsLabel() {
  return TmntText({ k: 'DRE_ANYTHING_ELSE_OPTIONAL', textOnly: true });
}

export default function ProductExchangeSurveyReturnReasons({
  product,
  onChange,
  selectedProductComments,
  canExchange,
  returnReasons,
}) {
  const tmnts = useContext(TmntContext);
  const [dreTextAreaCount, changeDreTextAreaCount] = useState(0);
  const returnReasonLabel = useReturnReasonLabel();
  const selectDetailedReasonLabel = useSelectDetailedReasonLabel();
  const tellUsMoreLabel = useTellUsMoreLabel();
  const exchangeCommentsLabel = useExchangeCommentsLabel();
  const comments = useMemo(
    () => selectedProductComments[product.orderLineKey],
    [selectedProductComments, product],
  );
  // TODO: bucket will be required once PIM is updated
  const bucket = useMemo(() => product.bucket || 'MISC_CATEGORIES', [product]);
  const dreTextareaMaxLength = 120;
  const defaultOption = useMemo(() => ({ value: '' }), []);

  const {
    showInvalidReturnReasons,
  } = useOutletContext();

  const additionalComments = useMemo(() => {
    if (!isSelectionValid(
      returnReasons,
      bucket,
      comments?.reason1?.value,
      comments?.reason2?.value,
      comments?.reason3?.value,
      comments?.reason4?.value,
    )) {
      return null;
    }

    if (comments?.exchangeComment) {
      changeDreTextAreaCount(comments.exchangeComment.length);
    }

    return (
      <span data-property="DRE_ADDITIONAL_COMMENTS">
        <Textarea
          id={`product-exchange-survey-comment-${product.primeLineKey}`}
          label={exchangeCommentsLabel}
          maxLength={dreTextareaMaxLength}
          value={comments?.exchangeComment}
          onChange={
          (e) => onChange(product.orderLineKey, {
            ...comments,
            exchangeComment: e.target.value,
          }, changeDreTextAreaCount(e.target.value.length))
        }
        />
        <span className="textarea dre-textarea">
          <small
            data-property="DRE_TEXTBOX_CHARCOUNT"
            className="maxlength"
            id={`product-exchange-survey-comment-${product.orderLineKey}-char-count`}
            role="alert"
            aria-live="polite"
            aria-atomic="true"
          >
            <TmntText k="DRE_TEXTBOX_CHARCOUNT" textOnly />
            <span className="char-count">
              {dreTextareaMaxLength - dreTextAreaCount}
            </span>
          </small>
        </span>
      </span>
    );
  }, [comments, onChange, dreTextAreaCount, exchangeCommentsLabel]);

  const returnReasonUI1 = useMemo(() => {
    if (!tmnts) return null;
    const returnReasonOptions = getProductReturnReasonOptions(returnReasons, bucket);
    const availableOptions = returnReasonOptions
      // NOTE: this is to randomize the options for first level for FUL-1337
      // there are already in tests in place for this since we moved the
      // randomization out of getProductReturnReasonOptions
      .sort(() => Math.random() - 0.5)
      .map((o) => ({
        label: tmnts[`DRE_${o.name}`]?.value,
        value: o.name,
      }));
    if (availableOptions.length === 0) return null;
    const reason1 = comments?.reason1 || defaultOption;
    return (
      <div className="return-reason">
        <DropDown
          id={`product-exchange-survey-reason-1-${product.primeLineKey}`}
          label={`*${returnReasonLabel}`}
          value={reason1.value}
          options={availableOptions}
          isInvalid={showInvalidReturnReasons && !reason1.value}
          onChange={
            (e) => {
              if (e.target.value === defaultOption.value) {
                return;
              }
              onChange(product.orderLineKey, {
                ...comments,
                bucket,
                reason1: availableOptions.find((o) => o.value === e.target.value),
                reason2: null,
                reason3: null,
              });
            }
          }
        >
          {showInvalidReturnReasons && !reason1.value
            ? (
              <ErrorMessage>
                <TmntText k="DRE_SELECTION_REQUIRED" />
              </ErrorMessage>
            ) : null}
        </DropDown>
      </div>
    );
  }, [
    comments,
    onChange,
    returnReasonLabel,
    returnReasons,
    bucket,
    defaultOption,
    tmnts,
    showInvalidReturnReasons,
  ]);

  const returnReasonUI2 = useMemo(() => {
    if (!tmnts) return null;
    if (!comments?.reason1) return null;
    const returnReasonOptions = getProductReturnReasonOptions(
      returnReasons,
      bucket,
      comments.reason1.value,
    );
    const availableOptions = returnReasonOptions.map((o) => ({
      label: tmnts[`DRE_${o.name}`]?.value,
      value: o.name,
    }));
    if (availableOptions.length === 0) return null;
    const reason2 = comments?.reason2 || { value: '' };
    return (
      <div className="return-reason">
        <DropDown
          id={`product-exchange-survey-reason-2-${product.primeLineKey}`}
          label={`*${selectDetailedReasonLabel}`}
          value={reason2.value}
          options={availableOptions}
          isInvalid={showInvalidReturnReasons && !reason2.value}
          onChange={
            (e) => {
              if (e.target.value === defaultOption.value) {
                return;
              }
              onChange(product.orderLineKey, {
                ...comments,
                bucket,
                reason2: availableOptions.find((o) => o.value === e.target.value),
                reason3: null,
              });
            }
          }
        >
          {showInvalidReturnReasons && !reason2.value
            ? <ErrorMessage><TmntText k="DRE_SELECTION_REQUIRED" /></ErrorMessage> : null}
        </DropDown>
      </div>
    );
  }, [
    comments,
    onChange,
    selectDetailedReasonLabel,
    returnReasons,
    bucket,
    defaultOption,
    tmnts,
    showInvalidReturnReasons,
  ]);

  const returnReasonUI3 = useMemo(() => {
    if (!tmnts) return null;
    if (!comments?.reason1?.value || comments?.reason1?.value === defaultOption.value) return null;
    if (!comments?.reason2?.value || comments?.reason2?.value === defaultOption.value) return null;
    const returnReasonOptions = getProductReturnReasonOptions(
      returnReasons,
      bucket,
      comments.reason1.value,
      comments.reason2.value,
    );
    const availableOptions = returnReasonOptions.map((o) => ({
      label: tmnts[`DRE_${o.name}`]?.value,
      value: o.name,
    }));
    if (availableOptions.length === 0) return null;
    const reason3 = comments?.reason3 || defaultOption;
    return (
      <div className="return-reason">
        <DropDown
          id={`product-exchange-survey-reason-3-${product.primeLineKey}`}
          label={`*${tellUsMoreLabel}`}
          value={reason3.value}
          options={availableOptions}
          isInvalid={showInvalidReturnReasons && !reason3.value}
          onChange={
            (e) => {
              if (e.target.value === defaultOption.value) {
                return;
              }
              onChange(product.orderLineKey, {
                ...comments,
                bucket,
                reason3: availableOptions.find((o) => o.value === e.target.value),
              });
            }
          }
        >
          {showInvalidReturnReasons && !reason3.value
            ? (
              <ErrorMessage>
                <TmntText k="DRE_SELECTION_REQUIRED" />
              </ErrorMessage>
            ) : null}
        </DropDown>
      </div>
    );
  }, [
    comments,
    onChange,
    tellUsMoreLabel,
    returnReasons,
    bucket,
    defaultOption,
    tmnts,
    showInvalidReturnReasons,
  ]);

  return (
    <div className="product-exchange-survey-wrapper" data-testid="product-exchange-survey-wrapper">
      <h4 className="help-us-get-it-right">
        <TmntText k="DRE_HELP_US_GET_IT_RIGHT" />
      </h4>
      <TmntText k="DRE_INDICATES_REQUIRED_FIELD" />
      {returnReasonUI1}
      {returnReasonUI2}
      {returnReasonUI3}
      {additionalComments}

      {canExchange && product.name && (
        <ExchangeButton product={product} k="DRE_EXCHANGE_FOR_NEW_SIZE_COLOR" />
      )}
    </div>
  );
}

ProductExchangeSurveyReturnReasons.propTypes = {
  product: PropTypes.shape({
    imageId: PropTypes.string,
    productPricePaidFmt: PropTypes.string,
    partNumber: PropTypes.string,
    name: PropTypes.string,
    productId: PropTypes.string,
    orderLineKey: PropTypes.string.isRequired,
    collection: PropTypes.string,
    exchange: PropTypes.shape({
      size: PropTypes.string,
      color: PropTypes.string,
    }),
    primeLineKey: PropTypes.string,
    bucket: PropTypes.string,
  }).isRequired,
  selectedProductComments: PropTypes.shape({
    exchangeReason: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
    exchangeSecondaryReason: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      type: PropTypes.string,
    }),
    exchangeComment: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  canExchange: PropTypes.bool.isRequired,
  returnReasons: returnReasonsShape.isRequired,
};
